import type { Rule } from 'antd/es/form';
import { emailListRule, validateEmailList } from '@/utils/emailList';
import type { UserType } from '@/pages/POS/types';

export const CCCD_PATTERN = /^\d{12}$/;

/**
 * Chuỗi thay cho CCCD khi nạp form từ đơn có CCCD đã mã hoá (BE trả ciphertext
 * `v1:…`). Đúng 12 ký tự bullet để lấp ô mà không lộ dữ liệu.
 */
export const MASKED_CCCD_DISPLAY = '••••••••••••';

/**
 * CCCD hiện ở form đang là giá trị che (chưa được user gõ lại): hoặc chuỗi bullet
 * hiển thị, hoặc ciphertext `v1:…` từ BE.
 *
 * Dùng để (a) miễn validate định dạng — không thể bắt gõ đủ 12 số một giá trị đã
 * che, và (b) omit khỏi payload update — gửi giá trị che lên BE sẽ ghi đè CCCD
 * thật bằng rác.
 */
export function isMaskedCccd(value: unknown): boolean {
  return typeof value === 'string' && (value === MASKED_CCCD_DISPLAY || value.startsWith('v1:'));
}
export const TAX_CODE_PATTERN = /^\d{10}(-\d{3})?$/;
// TCR-1835: hộ kinh doanh cá thể dùng CCCD (12 số) làm MST — dùng chung message
// cho mọi field MST của khách "Tổ chức" chấp nhận cả hai định dạng.
const ORG_TAX_CODE_FORMAT_MESSAGE =
  'Mã số thuế không đúng định dạng (10 chữ số, 10 số-3 số, hoặc 12 số CCCD hộ kinh doanh cá thể)';
// VN phone: mobile prefix 03/05/07/08/09 + 8 digits (10 total), or landline
// 02 + 8–9 digits (10–11 total). Rejects all-zero / invalid prefixes like 0000…
const PHONE_PATTERN = /^(0[35789]\d{8}|02\d{8,9})$/;

const citizenIdFormatRule: Rule = {
  validator: (_, value) => {
    if (!value) return Promise.resolve();
    if (CCCD_PATTERN.test(value) || isMaskedCccd(value)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error('CCCD phải đủ 12 chữ số'));
  },
};

/** POS có thể bỏ required cho khách cá nhân, nhưng giá trị được nhập vẫn phải hợp lệ. */
export function getCitizenIdRules(required: boolean): Rule[] {
  return [
    ...(required
      ? [{ required: true, message: 'CCCD bắt buộc theo quy định' } as Rule]
      : []),
    citizenIdFormatRule,
  ];
}

// Export bắt buộc cũ dành cho legacy/non-POS consumers (đặc biệt Orders/edit).
export const citizenIdRules: Rule[] = getCitizenIdRules(true);

const buyerCccdFormatRule: Rule = {
  validator: (_, value) => {
    if (!value) return Promise.resolve();
    if (CCCD_PATTERN.test(value) || isMaskedCccd(value)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error('CCCD người mua phải đủ 12 chữ số'));
  },
};

/**
 * TCR-2618 — "CCCD người mua" (`buyer_cccd`) KHÔNG còn bắt buộc ở bất kỳ ngữ
 * cảnh nào (POS tạo đơn, sửa đơn, dialog "Thông tin khách hàng", khách tổ
 * chức). Để trống là hợp lệ; nếu thu ngân có nhập thì vẫn phải là CCCD 12 số
 * (hoặc giá trị đã che `v1:`/bullet) — validator định dạng giữ nguyên.
 *
 * Vì vậy hàm này KHÔNG còn tham số `required`: không có ngữ cảnh nào bật/tắt
 * được luật bắt buộc cho field này nữa.
 */
export function getBuyerCccdRules(): Rule[] {
  return [buyerCccdFormatRule];
}

export const buyerCccdRules: Rule[] = getBuyerCccdRules();

export const customerPhoneRules: Rule[] = [
  { required: true, message: 'Vui lòng nhập số điện thoại' },
  {
    pattern: PHONE_PATTERN,
    message: 'SĐT không hợp lệ (đầu số VN: 03/05/07/08/09, hoặc cố định 02, 10–11 số)',
  },
];

export const customerBirthdayRules: Rule[] = [
  {
    validator: (_, value) => {
      if (!value) return Promise.resolve();
      const match = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (!match) {
        return Promise.reject(new Error('Ngày sinh phải có định dạng DD/MM/YYYY'));
      }
      const day = Number(match[1]);
      const month = Number(match[2]);
      const year = Number(match[3]);
      const date = new Date(year, month - 1, day);
      const isRealDate =
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day;
      if (!isRealDate) {
        return Promise.reject(new Error('Ngày sinh không hợp lệ'));
      }
      if (year < 1900 || date.getTime() > Date.now()) {
        return Promise.reject(new Error('Ngày sinh không hợp lệ'));
      }
      return Promise.resolve();
    },
  },
];

export const customerEmailRules: Rule[] = [
  { type: 'email', message: 'Email không đúng định dạng' },
];

// TCR-2432 — email nhận hoá đơn nhận DANH SÁCH (tối đa 5, cách nhau bằng
// phẩy/chấm phẩy/xuống dòng), gửi lên vẫn là một chuỗi `tax_email`.
export const invoiceEmailRules: Rule[] = [emailListRule()];

export const requiredRule = (label: string): Rule => ({
  required: true,
  message: `Vui lòng nhập ${label}`,
});

const invoiceTaxIdFormatRule: Rule = ({ getFieldValue }) => ({
  validator: (_, value) => {
    if (!value) return Promise.resolve();
    const userType = getFieldValue('user_type') as UserType | undefined;
    const isCccd = CCCD_PATTERN.test(value);
    const isTaxCode = TAX_CODE_PATTERN.test(value);
    const isMasked = isMaskedCccd(value);
    if (userType === 'personal') {
      if (isCccd || isTaxCode || isMasked) return Promise.resolve();
      return Promise.reject(
        new Error(
          'Phải là CCCD (12 số) hoặc mã số thuế hợp lệ (VD: 0101234567 hoặc 0101234567-001)',
        ),
      );
    }
    // TCR-1835: hộ kinh doanh cá thể (khai báo là "Tổ chức") dùng CCCD (12 số)
    // làm MST thay vì mã số thuế doanh nghiệp chuẩn — chấp nhận thêm isCccd.
    if (isTaxCode || isCccd || isMasked) return Promise.resolve();
    return Promise.reject(new Error(ORG_TAX_CODE_FORMAT_MESSAGE));
  },
});

/** Giữ validator CCCD/MST khi invoice cá nhân POS không còn bắt buộc. */
export function getInvoiceTaxIdRules(required: boolean): Rule[] {
  return [
    ...(required
      ? [{ required: true, message: 'Vui lòng nhập CCCD/MST' } as Rule]
      : []),
    invoiceTaxIdFormatRule,
  ];
}

// invoice_tax_id branches by user_type; legacy export remains required.
export const invoiceTaxIdRules: Rule[] = getInvoiceTaxIdRules(true);

export const deliveryDaysRules: Rule[] = [
  { required: true, message: 'Vui lòng nhập số ngày giao hàng' },
  { type: 'number', min: 1, message: 'Hạn giao phải lớn hơn 0' },
];

// Delivery recipient reuses the same VN phone shape as the customer phone.
const DELIVERY_PHONE_PATTERN = /^(0[35789]\d{8}|02\d{8,9})$/;

// Customer-section "Mã số thuế" (organization only). FE-only field.
export const customerTaxCodeRules: Rule[] = [
  { required: true, message: 'Vui lòng nhập mã số thuế' },
  {
    validator: (_, value) => {
      if (!value) return Promise.resolve();
      // TCR-1835: chấp nhận thêm CCCD 12 số cho hộ kinh doanh cá thể.
      if (TAX_CODE_PATTERN.test(value) || CCCD_PATTERN.test(value)) {
        return Promise.resolve();
      }
      return Promise.reject(new Error(ORG_TAX_CODE_FORMAT_MESSAGE));
    },
  },
];

export const deliveryPhoneRules: Rule[] = [
  { required: true, message: 'Vui lòng nhập số điện thoại người nhận' },
  { pattern: DELIVERY_PHONE_PATTERN, message: 'SĐT không hợp lệ' },
];

export const deliveryCitizenIdRules: Rule[] = [
  {
    validator: (_, value) => {
      // Không bắt buộc; nhưng nếu có thì phải là 12 số HOẶC giá trị đã che. Ô này
      // thường được copy từ citizen_id qua checkbox "Giống thông tin khách hàng",
      // nên khi sửa đơn cũ nó nhận luôn giá trị che — không miễn trừ sẽ báo đỏ oan.
      if (!value || CCCD_PATTERN.test(value) || isMaskedCccd(value)) {
        return Promise.resolve();
      }
      return Promise.reject(new Error('CCCD phải đủ 12 chữ số'));
    },
  },
];

export const deliveryEmailRules: Rule[] = [
  { type: 'email', message: 'Email không đúng định dạng' },
];

export const pickupStoreRules: Rule[] = [
  { required: true, message: 'Vui lòng chọn chi nhánh' },
];

export const RECEIVE_METHODS: { value: 'delivery' | 'pickup' | 'vault'; label: string }[] = [
  { value: 'delivery', label: 'Giao hàng' },
  { value: 'pickup', label: 'Lấy tại chi nhánh' },
  { value: 'vault', label: 'Lưu kho' },
];

// All field names that get cleared when user_type toggles.
export const FIELDS_RESET_ON_USER_TYPE_CHANGE = [
  'customer_phone',
  'customer_name',
  'citizen_id',
  'customer_email',
  'customer_address',
  'customer_city_id',
  'customer_ward_id',
  'customer_birthday',
  'description',
  'invoice_same_as_delivery',
  'invoice_name',
  'invoice_tax_id',
  'invoice_address',
  'invoice_email',
  'buyer_name',
  'buyer_cccd',
  'amis_account_object_id',
  'customer_tax_code',
] as const;

// Mọi field mà CustomerDialog quản lý — dùng cho snapshot/restore/clear.
export const CUSTOMER_DIALOG_FIELDS = [
  'customer_phone',
  'customer_name',
  'citizen_id',
  'customer_city_id',
  'customer_ward_id',
  'customer_address',
  'customer_email',
  'customer_birthday',
  'description',
  'care_staff_id',
  'delivery_days',
  'has_delivery',
  'user_type',
  'invoice_same_as_delivery',
  'invoice_name',
  'invoice_tax_id',
  'invoice_address',
  'invoice_email',
  'buyer_name',
  'buyer_cccd',
  'amis_account_object_id',
  'customer_user_id',
  // TCR-2129: kênh B2B gắn với khách — xoá khách phải xoá luôn kênh.
  '_b2bChannel',
  '_cityName',
  '_wardName',
  // TCR-1281: `group_id`/`_groupName` gắn với `customer_user_id` giống hệt
  // `_cityName`/`_wardName` — phải nằm CÙNG danh sách này để `clearCustomer()`
  // (đọc `CUSTOMER_DIALOG_FIELDS` để xoá trắng) dọn luôn nhóm của khách cũ,
  // tránh nhóm khách A rơi sang khách B/đơn kế tiếp khi bấm "xoá khách".
  'customer_group_id',
  '_groupName',
  // TCR-1953: mã KH AMIS — field hiển thị-only như `_groupName`, cùng lý do phải nằm ở đây.
  '_amisCode',
  'customer_tax_code',
  'receive_method',
  'pickup_store_id',
  'delivery_same_as_customer',
  /**
   * Đọc mười field người nhận dưới đây bằng `Form.useWatch` thì BẮT BUỘC dùng
   * `{ form, preserve: true }`, không phải dạng hai tham số:
   *
   *  - `delivery_name` / `delivery_phone` / `delivery_address` do
   *    `DeliveryRecipientFields` đăng ký, mà file đó chỉ mount BÊN TRONG
   *    `CustomerDialog`. Modal không có `forceRender`, nên khi thu ngân chưa từng
   *    mở dialog thì ba field đó chưa hề được đăng ký.
   *  - `_deliveryWardName` / `_deliveryCityName` KHÔNG có `Form.Item` nào trên
   *    toàn repo, chỉ được `setFieldValue`.
   *
   * `useWatch(name, form)` đọc `getFieldsValue()` — chỉ thấy field ĐANG MOUNT
   * (rc-field-form `useWatch.js` dùng `options.preserve ? allValues : values`).
   * Thiếu `preserve` ⇒ năm field nêu tên ở trên luôn `undefined` ở luồng phổ biến
   * nhất: tra SĐT nạp sẵn khách, không mở dialog.
   *
   * KHÔNG sửa bằng cách thêm chúng vào `FULFILLMENT_REGISTERED_FIELDS`
   * (`Fulfillment/FulfillmentFormSection.tsx`): sẽ tạo `Form.Item` trùng tên với
   * item mang `rules` của `DeliveryRecipientFields`.
   */
  'delivery_name',
  'delivery_phone',
  'delivery_citizen_id',
  'delivery_email',
  'delivery_city_id',
  'delivery_ward_id',
  'delivery_address',
  'delivery_note',
  '_deliveryCityName',
  '_deliveryWardName',
  // TCR-2128 — nguồn của địa chỉ giao nhận (entry nào trong Sổ địa chỉ) + ô "Lưu vào Sổ địa chỉ":
  // phải được chụp/khôi phục cùng các field `delivery_*` để Huỷ không để lại nguồn lệch.
  '_deliveryAddressSource',
  'delivery_save_to_book',
  // TCR-2384 — ô "Thêm vào người nhận khác" + cờ ghi chú đã sửa tay (chặn tự điền đè chữ tay).
  'delivery_add_receiver_to_book',
  '_deliveryNoteTouched',
  'shipping_mode',
  'shipping_fee_payer',
  'shipping_fee',
  'prepaid_amount',
  'debt_amount',
  'shipping_partner',
  'shipping_note',
  'shipping_label',
  'shipping_promo_code',
  'package_weight',
  'package_length',
  'package_width',
  'package_height',
  'package_info_initialized',
  'shipping_insurance_enabled',
  'shipping_insurance_value',
] as const;

/**
 * Các field mà CustomerDialog thực sự validate khi bấm Xác nhận.
 * Dùng chung cho cả nút Xác nhận và luồng chọn khách từ ô tìm kiếm để tránh
 * hai đường thao tác có tiêu chí "đủ thông tin" khác nhau.
 */
export function customerDialogValidationFields(
  userType: UserType,
  receiveMethod: 'delivery' | 'pickup' | 'vault' = 'delivery',
): string[] {
  const identityField = userType === 'personal' ? 'citizen_id' : 'customer_tax_code';
  const deliveryFields = receiveMethod === 'delivery'
    ? ['delivery_name', 'delivery_phone']
    : [];

  return [
    'customer_name',
    'customer_phone',
    identityField,
    'customer_email',
    'customer_city_id',
    'customer_ward_id',
    'customer_address',
    'customer_birthday',
    'description',
    ...deliveryFields,
    'invoice_name',
    'invoice_tax_id',
    'invoice_address',
    'invoice_email',
    'buyer_name',
    'buyer_cccd',
  ];
}

function hasValue(value: unknown): boolean {
  return typeof value === 'string' ? value.trim().length > 0 : value != null;
}

function isValidBirthday(value: unknown): boolean {
  if (!hasValue(value)) return true;
  const match = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return false;
  const date = new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
  return (
    date.getFullYear() === Number(match[3]) &&
    date.getMonth() === Number(match[2]) - 1 &&
    date.getDate() === Number(match[1]) &&
    Number(match[3]) >= 1900 &&
    date.getTime() <= Date.now()
  );
}

/** Kiểm tra nhanh trước khi quyết định có cần mở CustomerDialog hay không. */
export function isCustomerDialogComplete(
  values: Record<string, unknown>,
  userType: UserType,
  receiveMethod: 'delivery' | 'pickup' | 'vault',
  personalCccdRequired: boolean,
  personalInvoiceOptional = true,
): boolean {
  const identity = userType === 'personal' ? values.citizen_id : values.customer_tax_code;
  const invoiceTaxId = String(values.invoice_tax_id ?? '');
  const requiredValues = [
    values.customer_name,
    values.customer_phone,
    values.customer_city_id,
    values.customer_ward_id,
    values.customer_address,
  ];
  if (userType === 'personal') {
    if (personalCccdRequired) requiredValues.push(identity);
    if (!personalInvoiceOptional) {
      requiredValues.push(
        values.invoice_name,
        values.invoice_tax_id,
        values.invoice_address,
      );
    }
  } else {
    requiredValues.push(
      identity,
      values.invoice_name,
      values.invoice_tax_id,
      values.invoice_address,
    );
    // TCR-2618 — `buyer_cccd` (CCCD người mua) KHÔNG còn nằm trong danh sách
    // trường bắt buộc: để trống vẫn xác nhận/lưu được ở mọi ngữ cảnh.
  }
  if (receiveMethod === 'delivery') {
    requiredValues.push(values.delivery_name, values.delivery_phone);
  }
  if (!requiredValues.every(hasValue)) return false;

  const identityValid = userType === 'personal'
    ? !hasValue(identity) ||
      CCCD_PATTERN.test(String(identity)) ||
      isMaskedCccd(identity)
    // TCR-1835: tổ chức là hộ kinh doanh cá thể có thể dùng CCCD làm MST.
    : TAX_CODE_PATTERN.test(String(identity)) || CCCD_PATTERN.test(String(identity));
  const invoiceTaxValid = userType === 'personal'
    ? !hasValue(invoiceTaxId) ||
      CCCD_PATTERN.test(invoiceTaxId) ||
      TAX_CODE_PATTERN.test(invoiceTaxId) ||
      isMaskedCccd(invoiceTaxId)
    : TAX_CODE_PATTERN.test(invoiceTaxId) || CCCD_PATTERN.test(invoiceTaxId);
  const buyerCccdValid =
    !hasValue(values.buyer_cccd) ||
    CCCD_PATTERN.test(String(values.buyer_cccd)) ||
    isMaskedCccd(values.buyer_cccd);
  const phoneValid = PHONE_PATTERN.test(String(values.customer_phone));
  const deliveryPhoneValid = receiveMethod !== 'delivery' || DELIVERY_PHONE_PATTERN.test(String(values.delivery_phone));
  const customerEmailValid =
    !hasValue(values.customer_email) || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(values.customer_email));
  // TCR-2432 — `invoice_email` là danh sách, dùng chung luật với validator của
  // ô nhập để nút "Xác nhận" không bị chặn/lọt khác với thông báo dưới ô.
  const invoiceEmailValid =
    validateEmailList(typeof values.invoice_email === 'string' ? values.invoice_email : '') === null;
  const emailsValid = customerEmailValid && invoiceEmailValid;

  return (
    identityValid &&
    invoiceTaxValid &&
    buyerCccdValid &&
    phoneValid &&
    deliveryPhoneValid &&
    emailsValid &&
    isValidBirthday(values.customer_birthday)
  );
}

/**
 * Nhóm field do thanh phương thức nhận hàng (`FulfillmentFormSection`) sở hữu —
 * KHÔNG còn thuộc `CustomerDialog`.
 *
 * `CUSTOMER_DIALOG_FIELDS` ở trên vẫn phải chứa đủ nhóm này: nó là danh sách
 * snapshot/restore/**clear**, và `clearCustomer()` dùng nó để dọn form khi bấm
 * "xoá khách". Bỏ nhóm vận chuyển ra khỏi đó ⇒ dữ liệu vận chuyển của khách A
 * sống sót qua nút "xoá khách" và rơi vào đơn kế tiếp.
 *
 * Hằng này chỉ dùng để LOẠI TRỪ khỏi ảnh chụp phục vụ nút "Huỷ" của dialog: nút
 * đó không được hoàn nguyên phương thức nhận hàng mà thu ngân vừa bấm ở thanh
 * dưới — hai thứ đó giờ nằm ngoài dialog.
 */
export const BAR_OWNED_FIELDS = [
  'receive_method',
  'pickup_store_id',
  'shipping_mode',
  'shipping_fee_payer',
  'shipping_fee',
  'prepaid_amount',
  'debt_amount',
  'shipping_partner',
  'shipping_note',
  'shipping_label',
  'shipping_promo_code',
  'package_weight',
  'package_length',
  'package_width',
  'package_height',
  'package_info_initialized',
  'shipping_insurance_enabled',
  'shipping_insurance_value',
] as const;

/** `CUSTOMER_DIALOG_FIELDS` trừ nhóm thanh PTNH — dùng cho snapshot nút "Huỷ". */
export const CANCEL_SNAPSHOT_FIELDS = CUSTOMER_DIALOG_FIELDS.filter(
  (f) => !(BAR_OWNED_FIELDS as readonly string[]).includes(f),
);

/**
 * Field BẮT BUỘC được render NGOÀI `CustomerDialog` — tên hằng cũ
 * `SIDEBAR_REQUIRED_FIELDS` nói "SIDEBAR" vì lúc đầu cả nhóm đều ở panel Thông
 * tin đơn hàng, nhưng nghĩa đúng là "ngoài dialog", không riêng sidebar (ví dụ
 * `pickup_store_id` nằm ở thanh phương thức nhận hàng dưới đáy, không phải
 * sidebar) — đổi tên cho khớp nghĩa:
 *  - `delivery_days` — panel Thông tin đơn hàng (variant compact).
 *  - `pickup_store_id` — thuộc THANH PHƯƠNG THỨC NHẬN HÀNG neo đáy cột hàng
 *    (`FulfillmentBar`): ô chọn chi nhánh. Thiếu nó mà bật dialog thì thu ngân
 *    thấy dialog mở ra không ô nào đỏ (từ Task 5 dialog không còn đăng ký
 *    `receive_method`/`shipping_*`/`pickup_*`), còn ô thật thì nằm dưới lớp mask
 *    của Modal (`maskClosable={false}`).
 *
 *  - `care_staff_id` — Task 7 hiện lại dropdown "Nhân viên chăm sóc", nhưng đặt
 *    NGOÀI `CustomerDialog` (field độc lập ở cột phải/panel Thông tin đơn
 *    hàng, xem `POS/index.tsx` và `Orders/edit.tsx`), cùng vị trí với
 *    `delivery_days`. Final review fix round 2 (theo sau I2 — POS thêm
 *    `requiredRule` cho field này): rule required mới khiến `care_staff_id` có
 *    thể xuất hiện trong `errorFields` của `form.validateFields()` lần đầu
 *    tiên. Thiếu tên field này trong mảng thì `requiresCustomerDialog` mở
 *    nhầm `CustomerDialog` — dialog đó KHÔNG chứa field này (chưa từng, kể cả
 *    trước Task 7) — và `scrollToField('care_staff_id')` nhắm vào một phần tử
 *    nằm dưới lớp mask của Modal, không bao giờ tới được. Cùng đúng lớp lỗi
 *    mà `pickup_store_id` đã gặp ở trên.
 *
 * Task 7b: BỎ `shipping_mode` khỏi danh sách này. Trước đây nó là "phao dự
 * phòng" cho effect chuẩn hoá trong `FulfillmentFormSection` — effect đó đã bị
 * xoá, và `missingCustomerFields` KHÔNG còn bao giờ báo thiếu `shipping_mode`
 * (biên đọc `effectiveShippingMode` coi rỗng là `'self'`), nên tên field này
 * không còn xuất hiện trong `fieldNames` truyền vào `requiresCustomerDialog`
 * nữa — giữ nó trong mảng chỉ còn là code chết.
 *
 * `CUSTOMER_DIALOG_FIELDS` ở trên là danh sách snapshot/restore/clear nên vẫn
 * chứa cả `shipping_mode` và `care_staff_id`; đừng dùng nó để quyết định có mở
 * dialog hay không.
 */
export const FIELDS_OUTSIDE_CUSTOMER_DIALOG = [
  'delivery_days',
  'pickup_store_id',
  'care_staff_id',
] as const;

/**
 * Có cần bật CustomerDialog để user thấy/sửa được các field lỗi không.
 *
 * antd chỉ validate và tô đỏ Form.Item ĐANG MOUNT. Field nằm trong dialog thì
 * phải mở dialog mới hiện đỏ; field đã render sẵn ngoài dialog
 * (`FIELDS_OUTSIDE_CUSTOMER_DIALOG`) thì đỏ ngay tại chỗ — bật dialog lúc đó chỉ
 * làm phiền, và với `pickup_store_id` còn che mất đúng ô cần sửa.
 */
export function requiresCustomerDialog(fieldNames: string[]): boolean {
  return fieldNames.some(
    (name) => !(FIELDS_OUTSIDE_CUSTOMER_DIALOG as readonly string[]).includes(name),
  );
}
